import { ArrowRightLeft } from "../../../../components/icons";
import { useTranslation } from "react-i18next";

/**
 * Per-team accent for a substitution bar — sourced from the same
 * `resolveTeamPalette` (`src/lib/teamPalette.ts`) that TacticalPlayerNode
 * uses for the on-field "bolitas", so the bar visually matches that team's
 * jersey-number markers instead of a fixed red/blue pair that only matched
 * football's traditional home/away coloring by coincidence (client request
 * — PMA-COCK, iPad viewport wave).
 *
 * `text` is `TeamPalette.textOnDark` — a WCAG-AA-safe (>=4.5:1) shade of the
 * team's primary color already computed against the dark cockpit panel
 * background, so a team whose primary color is white/very light still
 * renders a legible label instead of the raw (often illegible) primary hex.
 */
export interface SubstitutionBarAccent {
  /** Translucent tint of the team's primary color for the bar background. */
  background: string;
  /** Team's soft border color (same token ScoreBoard's goal chips use). */
  border: string;
  /** Contrast-safe text/icon color for this background. */
  text: string;
}

interface QuickSubstitutionPanelProps {
  onHomeSubstitution: () => void;
  onAwaySubstitution: () => void;
  homeTeamName: string;
  awayTeamName: string;
  /** Falls back to the legacy static red accent when omitted. */
  homeAccent?: SubstitutionBarAccent;
  /** Falls back to the legacy static blue accent when omitted. */
  awayAccent?: SubstitutionBarAccent;
  disabled?: boolean;
}

// Legacy static colors — only used as a defensive fallback if a caller ever
// renders this panel without resolved team colors (match not yet loaded).
const FALLBACK_HOME_ACCENT: SubstitutionBarAccent = {
  background: "rgba(127, 29, 29, 0.3)",
  border: "rgba(239, 68, 68, 0.3)",
  text: "#fecaca",
};
const FALLBACK_AWAY_ACCENT: SubstitutionBarAccent = {
  background: "rgba(30, 58, 138, 0.3)",
  border: "rgba(59, 130, 246, 0.3)",
  text: "#dbeafe",
};

const QuickSubstitutionPanel = ({
  onHomeSubstitution,
  onAwaySubstitution,
  homeTeamName,
  awayTeamName,
  homeAccent,
  awayAccent,
  disabled = false,
}: QuickSubstitutionPanelProps) => {
  const { t } = useTranslation("logger");
  const home = homeAccent ?? FALLBACK_HOME_ACCENT;
  const away = awayAccent ?? FALLBACK_AWAY_ACCENT;

  return (
    <div className="grid grid-cols-2 gap-4 mt-2">
      <button
        type="button"
        onClick={onHomeSubstitution}
        disabled={disabled}
        data-testid="btn-sub-home"
        className="flex items-center justify-center gap-2 px-4 py-3 border rounded-lg hover:brightness-125 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter,opacity]"
        style={{
          backgroundColor: home.background,
          borderColor: home.border,
          color: home.text,
        }}
      >
        <ArrowRightLeft size={18} />
        <span className="font-medium truncate">
          {t("substitutionAction", { team: homeTeamName })}
        </span>
      </button>
      <button
        type="button"
        onClick={onAwaySubstitution}
        disabled={disabled}
        data-testid="btn-sub-away"
        className="flex items-center justify-center gap-2 px-4 py-3 border rounded-lg hover:brightness-125 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter,opacity]"
        style={{
          backgroundColor: away.background,
          borderColor: away.border,
          color: away.text,
        }}
      >
        <ArrowRightLeft size={18} />
        <span className="font-medium truncate">
          {t("substitutionAction", { team: awayTeamName })}
        </span>
      </button>
    </div>
  );
};

export default QuickSubstitutionPanel;
